import { useState, useRef, useCallback, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LoaderIcon, PencilIcon, TrashIcon } from '@/components/icons';
import { toast } from 'sonner';
import { fullErrorText } from '@/api/errors';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { applicationApi } from '@/api/application.api';
import { create } from '@bufbuild/protobuf';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import { InstanceStatus, ConfigSyncStatus } from '@/proto/agentplatform/v1/instance_pb';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';

function formatTimestamp(timestamp: number | bigint | undefined) {
  if (!timestamp) return '-';
  const date = new Date(Number(timestamp) / 1000000);
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function HoverTooltip({ text, children }: { text: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const updatePos = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const maxTooltipPx = 320;
    const pad = 8;
    let left = r.left;
    const vw = window.innerWidth;
    if (left + maxTooltipPx + pad > vw) {
      left = Math.max(pad, vw - maxTooltipPx - pad);
    }
    setPos({ top: r.bottom + 6, left });
  }, []);
  useEffect(() => {
    if (!open) return;
    updatePos();
    const onMove = () => updatePos();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, updatePos]);
  return (
    <>
      <span
        ref={triggerRef}
        className="inline-flex shrink-0 cursor-default items-center justify-center rounded p-0.5 text-muted-foreground outline-none hover:bg-muted/70 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
        tabIndex={0}
        aria-label={`说明：${text}`}
        onMouseEnter={() => {
          updatePos();
          setOpen(true);
        }}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => {
          updatePos();
          setOpen(true);
        }}
        onBlur={() => setOpen(false)}
      >
        {children}
      </span>
      {open
        ? createPortal(
            <div
              role="tooltip"
              className="pointer-events-none fixed z-[200] max-w-sm rounded-md border border-border bg-popover px-2.5 py-1.5 text-xs leading-snug text-popover-foreground shadow-md [max-width:min(20rem,calc(100vw-1rem))] whitespace-pre-wrap break-words"
              style={{ top: pos.top, left: pos.left }}
            >
              {text}
            </div>,
            document.body
          )
        : null}
    </>
  );
}

function AppStatusBadge({
  status,
  configSyncStatus,
}: {
  status: InstanceStatus;
  configSyncStatus: ConfigSyncStatus;
}) {
  if (status === InstanceStatus.RUNNING) {
    return (
      <div className="inline-flex items-center gap-1.5">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium">
          <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
          运行中
        </span>
        {configSyncStatus === ConfigSyncStatus.OUTDATED && (
          <HoverTooltip text="智能体模版更新，实例需手动更新">
            <svg
              className="h-3.5 w-3.5 text-foreground"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </HoverTooltip>
        )}
      </div>
    );
  }
  if (status === InstanceStatus.PENDING) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium">
        <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
        待启动
      </span>
    );
  }
  return <span className="text-xs text-muted-foreground">-</span>;
}

export default function AgentApps() {
  const { id: agentId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [searchKeyword, setSearchKeyword] = useState('');
  const [deleteUid, setDeleteUid] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // 关键字变化后回到首页，避免停留在超出范围的页码
  useEffect(() => {
    setPage(1);
  }, [searchKeyword]);

  const { data, isLoading } = useQuery({
    queryKey: ['applications', agentId, page, pageSize, searchKeyword],
    queryFn: () =>
      applicationApi.list(
        create(ListRequestSchema, {
          page,
          pageSize,
          filter: agentId
            ? {
                agent_id: agentId,
              }
            : {},
          fuzzy: searchKeyword.trim() ? { name: searchKeyword.trim() } : {},
        })
      ),
  });

  const deleteMutation = useMutation({
    mutationFn: (uid: string) => applicationApi.delete(uid),
    onSuccess: () => {
      setDeleteUid(null);
      queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
    onError: (error) => {
      toast.error(`删除失败：${fullErrorText(error)}`);
    },
  });

  const agentApps = (data?.data || []).filter(Boolean);

  const keyword = searchKeyword.trim();
  const totalCount = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        {/* <div>
          <h1 className="text-2xl font-bold tracking-tight">实例列表</h1>
          <p className="text-muted-foreground mt-1">智能体模版关联的实例列表</p>
        </div> */}
        <div className="flex items-center gap-3">
          <Input
            placeholder="按名称搜索..."
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            className="w-52"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="border rounded-lg">
          <div className="p-4 space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-32" />
              </div>
            ))}
          </div>
        </div>
      ) : agentApps.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <svg
                className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                />
              </svg>
              <p className="text-lg font-medium mb-2">
                {keyword ? '没有找到匹配的应用' : '暂无应用'}
              </p>
              <p className="text-sm text-muted-foreground">
                {keyword ? '尝试其他关键词搜索' : '该 Agent 暂未关联任何应用'}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div>
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>名称</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead>描述</TableHead>
                  <TableHead>创建时间</TableHead>
                  <TableHead>更新时间</TableHead>
                  <TableHead>操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {agentApps.map((item) => (
                  <TableRow
                    key={item.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() =>
                      navigate(`/agents/${agentId}/apps/${item.id}`, {
                        state: { returnTo: location.pathname },
                      })
                    }
                  >
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell>
                      <AppStatusBadge
                        status={item.status}
                        configSyncStatus={item.configSyncStatus}
                      />
                    </TableCell>
                    <TableCell className="text-muted-foreground max-w-xs truncate">
                      {item.description || '-'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatTimestamp(item.createdAt)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatTimestamp(item.updatedAt)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/applications/${item.id}/edit`, {
                              state: { returnTo: location.pathname },
                            });
                          }}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteUid(item.id);
                          }}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={6} className="bg-muted/30">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">
                        共 {totalCount} 条，第 {page} / {totalPages} 页
                      </span>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm text-muted-foreground">每页</span>
                          <select
                            value={pageSize}
                            onChange={(e) => {
                              setPageSize(Number(e.target.value));
                              setPage(1);
                            }}
                            className="h-7 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
                          >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                          <span className="text-sm text-muted-foreground">条</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 w-7 p-0"
                            disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15 19l-7-7 7-7"
                              />
                            </svg>
                          </Button>
                          <div className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-background border text-sm">
                            <span className="font-medium">{page}</span>
                            <span className="text-muted-foreground">/</span>
                            <span className="text-muted-foreground">{totalPages}</span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 w-7 p-0"
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => p + 1)}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <AlertDialog open={!!deleteUid} onOpenChange={() => setDeleteUid(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>确定要删除该应用吗？此操作不可撤销。</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteUid(null)}>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteUid && deleteMutation.mutate(deleteUid)}
              disabled={deleteMutation.isPending}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleteMutation.isPending && <LoaderIcon className="mr-2 h-4 w-4" />}
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
